import * as React from "react";
import {
    Button,
    FormButton,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SearchInput,
} from "@jhool-io/fe-components";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useDebounce } from "../../../../hooks/helpers";
import { useFetchUsersList } from "../../../user-management/hooks/user-management.queries";
import { useFetchClientList } from "../../../../hooks/queries/client";
import ListState from "../../../../components/ListState/ListState";
import CheckPrimaryColorIcon from "../../../../components/Icons/CheckPrimaryColor";
import useToast from "../../../../hooks/useToast";
import { useAddUserRestriction } from "../../hooks/client-chart-restriction.mutations";
import Loader from "../../../../shared-ui/Loader/Loader";

interface AddRestrictionFormProps {
    onFormSubmit(): void;
}

export default function AddRestrictionForm({
    onFormSubmit,
}: AddRestrictionFormProps) {
    const [userToRestrictId, setUserToRestrictId] = React.useState<
        string | null
    >(null);
    const [userSearchString, setUserSearchString] = React.useState("");
    const [clientToRestrictId, setClientToRestrictId] = React.useState<
        string | null
    >(null);
    const [clientSearchString, setClientSearchString] = React.useState("");
    const [showUsersListPopover, setShowUsersListPopover] =
        React.useState(false);
    const [showClientsListPopover, setShowClientsListPopover] =
        React.useState(false);

    const usersListContainerRef = React.useRef<HTMLDivElement>(null);
    const clientsListContainerRef = React.useRef<HTMLDivElement>(null);

    const debounceUserSearchString = useDebounce(userSearchString);
    const debounceClientSearchString = useDebounce(clientSearchString);

    const { toast } = useToast();

    const { mutate } = useAddUserRestriction();

    const queryClient = useQueryClient();

    const { handleSubmit } = useForm({
        mode: "onChange",
    });

    const internalUsersList = useFetchUsersList({
        page: 1,
        limit: 20,
        search_string: debounceUserSearchString,
    });

    const clientsList = useFetchClientList({
        page: 1,
        limit: 20,
        search_string: debounceClientSearchString,
    });

    const selectedClientDetails = clientsList?.data?.data.find(
        (client) => client.client_id === clientToRestrictId
    );

    const selectedUserDetails = internalUsersList?.data?.data.find(
        (user) => user.user_id === userToRestrictId
    );

    const onSubmit = () => {
        if (!userToRestrictId || !clientToRestrictId) {
            toast({
                mode: "error",
                message: "Fields cannot be empty",
            });
            return;
        }

        const dataToSend = {
            client_ids: [clientToRestrictId],
            user_id: userToRestrictId,
        };

        mutate(dataToSend, {
            onSuccess: (res) => {
                queryClient.invalidateQueries({
                    queryKey: ["restricted-users"],
                });
                toast({
                    mode: "success",
                    message: res.message || "Restriction added successfully",
                });

                onFormSubmit();
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not add restriction at this time",
                });
            },
        });
    };

    return (
        <form
            id="add-restriction-form"
            name="add-restriction-form"
            onSubmit={handleSubmit(onSubmit)}
        >
            <div className="fg" ref={clientsListContainerRef}>
                <Popover
                    open={showClientsListPopover}
                    onOpenChange={(state) => setShowClientsListPopover(state)}
                >
                    <PopoverTrigger
                        asChild
                        onClick={() => setShowClientsListPopover(true)}
                    >
                        <FormButton
                            className={
                                selectedClientDetails
                                    ? "capitalize"
                                    : "normal-case"
                            }
                            value={
                                selectedClientDetails
                                    ? `${selectedClientDetails.first_name} ${selectedClientDetails.last_name}`
                                    : ""
                            }
                            placeholder="Client name"
                            label="Client name"
                        />
                    </PopoverTrigger>
                    <PopoverContent
                        className="popover-full"
                        align="start"
                        container={clientsListContainerRef.current}
                    >
                        <SearchInput
                            placeholder="Search for client"
                            onChange={(e) =>
                                setClientSearchString(e.target.value)
                            }
                        />

                        <div className="flex flex-col items-start max-h-[230px] overflow-y-auto">
                            {clientsList.isLoading && (
                                <Loader wrapperClassName="h-[150px]" />
                            )}
                            {clientsList.error && (
                                <ListState
                                    isError
                                    context="general"
                                    stateHelperText="Please try again later"
                                    errorMsg={
                                        clientsList.error?.response?.data
                                            ?.message || "Error loading clients"
                                    }
                                    cardClass="150px"
                                />
                            )}
                            {clientsList.data?.data && clientsList.isSuccess && (
                                <>
                                    {clientsList?.data?.data.map((client) => (
                                        <Button
                                            key={client.client_id}
                                            size="auto"
                                            variant="can-hover"
                                            className="font-medium capitalize w-full justify-start my-1.5 shrink-0"
                                            onClick={() => {
                                                setClientToRestrictId(
                                                    client.client_id
                                                );
                                                setClientSearchString("");
                                                setShowClientsListPopover(
                                                    false
                                                );
                                            }}
                                        >
                                            {`${client.first_name} ${client.last_name}`}
                                        </Button>
                                    ))}
                                    <Button
                                        size="auto"
                                        variant="can-hover"
                                        className="font-medium capitalize w-full justify-start my-1.5 shrink-0"
                                        onClick={() => {
                                            setClientToRestrictId("");
                                            setClientSearchString("");
                                            setShowClientsListPopover(false);
                                        }}
                                    >
                                        Clear
                                    </Button>
                                </>
                            )}
                        </div>
                    </PopoverContent>
                </Popover>
            </div>
            <div ref={usersListContainerRef}>
                <Popover
                    open={showUsersListPopover}
                    onOpenChange={(state) => setShowUsersListPopover(state)}
                >
                    <PopoverTrigger
                        asChild
                        onClick={() => setShowUsersListPopover(true)}
                    >
                        <FormButton
                            value={
                                selectedUserDetails
                                    ? `${selectedUserDetails.first_name} ${selectedUserDetails.last_name}`
                                    : ""
                            }
                            placeholder="Restrict user"
                            label="Restrict user"
                            className={
                                selectedUserDetails
                                    ? "uppercase"
                                    : "normal-case"
                            }
                        />
                    </PopoverTrigger>
                    <PopoverContent
                        className=" popover-full"
                        align="start"
                        container={usersListContainerRef.current}
                    >
                        <SearchInput
                            placeholder="Search for internal user"
                            onChange={(e) =>
                                setUserSearchString(e.target.value)
                            }
                        />

                        <div className="flex flex-col items-start max-h-[230px] overflow-y-auto">
                            {internalUsersList.isLoading && (
                                <Loader wrapperClassName="h-[150px]" />
                            )}
                            {internalUsersList.error && (
                                <ListState
                                    isError
                                    context="general"
                                    stateHelperText="Please try again later"
                                    errorMsg={
                                        internalUsersList.error?.response?.data
                                            ?.message ||
                                        "Error loading internal users"
                                    }
                                    cardClass="h-[150px]"
                                />
                            )}
                            {internalUsersList.data?.data &&
                                internalUsersList.isSuccess && (
                                    <>
                                        {internalUsersList?.data?.data.map(
                                            (user) => (
                                                <Button
                                                    key={user.user_id}
                                                    size="auto"
                                                    variant="can-hover"
                                                    className="font-medium capitalize w-full justify-start my-1.5 shrink-0"
                                                    onClick={() => {
                                                        setUserToRestrictId(
                                                            user.user_id
                                                        );
                                                        setUserSearchString("");
                                                        setShowUsersListPopover(
                                                            false
                                                        );
                                                    }}
                                                >
                                                    {`${user.first_name} ${user.last_name}`}
                                                    {userToRestrictId ===
                                                    user.user_id ? (
                                                        <CheckPrimaryColorIcon />
                                                    ) : null}
                                                </Button>
                                            )
                                        )}
                                        <Button
                                            size="auto"
                                            variant="can-hover"
                                            className="font-medium capitalize w-full justify-start my-1.5 shrink-0"
                                            onClick={() => {
                                                setUserToRestrictId("");
                                                setUserSearchString("");
                                                setShowUsersListPopover(false);
                                            }}
                                        >
                                            Clear
                                        </Button>
                                    </>
                                )}
                        </div>
                    </PopoverContent>
                </Popover>
            </div>
        </form>
    );
}
